import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Avatar,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
  Stack,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { enhancedApi } from '../api';
import SettingsPageLayout from './settings/SettingsPageLayout';
import { useSettingsForm } from './SettingsFormContext';

const Profile = () => {
  const { setIsDirty } = useSettingsForm();
  const location = useLocation();
  const navigate = useNavigate();
  const isInSettings = location.pathname.includes('/settings');
  const useSettingsLayout = isInSettings;
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveToast, setSaveToast] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordError, setPasswordError] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    profile_name: '',
    email: '',
    phone: '',
    timezone: '',
    role: '',
    last_login: '',
    status: 'Active',
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const userId = location.state?.userId || new URLSearchParams(location.search).get('user');
        const response = await enhancedApi.getUserProfile(userId);

        if (response.data.success) {
          const profileData = response.data.data;
          setUserProfile(profileData);
          setForm({
            first_name: profileData.first_name || '',
            last_name: profileData.last_name || '',
            profile_name: profileData.profile_name || '',
            email: profileData.email || '',
            phone: profileData.phone || '',
            timezone: profileData.timezone || '',
            role: profileData.role || '',
            last_login: profileData.last_login || '',
            status: profileData.status || 'Active',
          });
        } else {
          setError(response.data.error || 'Failed to fetch user profile');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch user profile');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [location.state?.userId, location.search]);

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setIsDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const userId = location.state?.userId || new URLSearchParams(location.search).get('user');
      const response = await enhancedApi.updateUserProfile(userId, form);

      if (response.data.success) {
        setIsDirty(false);
        setSaveToast(true);
        const refreshResponse = await enhancedApi.getUserProfile(userId);
        if (refreshResponse.data.success) {
          setUserProfile(refreshResponse.data.data);
        }
      } else {
        setError(response.data.error || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsDirty(false);
    if (userProfile) {
      setForm({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        profile_name: userProfile.profile_name || '',
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        timezone: userProfile.timezone || '',
        role: userProfile.role || '',
        last_login: userProfile.last_login || '',
        status: userProfile.status || 'Active',
      });
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handlePasswordSave = async () => {
    setPasswordError(null);
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    try {
      const userId = location.state?.userId || new URLSearchParams(location.search).get('user');
      const response = await enhancedApi.updateUserProfile(userId, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      if (response.data.success) {
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        setSaveToast(true);
      } else {
        setPasswordError(response.data.error || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    }
  };

  const cardSx = {
    p: 3,
    width: '100%',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 'none',
    bgcolor: 'background.paper',
  };

  const loadingContent = (
    <Box display="flex" justifyContent="center" alignItems="center" py={8}>
      <CircularProgress />
    </Box>
  );

  const errorContent = (
    <Paper sx={cardSx}>
      <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      <Button variant="contained" onClick={handleBack}>Go Back</Button>
    </Paper>
  );

  const noDataContent = (
    <Paper sx={cardSx}>
      <Typography variant="h6" sx={{ mb: 2 }}>No user data available.</Typography>
      <Button variant="contained" onClick={handleBack}>Go Back</Button>
    </Paper>
  );

  if (loading) {
    return useSettingsLayout ? (
      <SettingsPageLayout title="My Profile" description="Your name, contact info, and preferences." noCard>
        {loadingContent}
      </SettingsPageLayout>
    ) : loadingContent;
  }

  if (error && !userProfile) {
    return useSettingsLayout ? (
      <SettingsPageLayout title="My Profile" description="Your name, contact info, and preferences." noCard>
        {errorContent}
      </SettingsPageLayout>
    ) : errorContent;
  }

  if (!userProfile) {
    return useSettingsLayout ? (
      <SettingsPageLayout title="My Profile" description="Your name, contact info, and preferences." noCard>
        {noDataContent}
      </SettingsPageLayout>
    ) : noDataContent;
  }

  const initials =
    (form.first_name?.[0] || userProfile.username?.[0] || '').toUpperCase() +
    (form.last_name?.[0] || '').toUpperCase();

  const profileContent = (
    <Stack spacing={3}>
      {/* Avatar & Identity Card */}
      <Paper sx={cardSx}>
        <Box display="flex" alignItems="center" gap={3}>
          {!useSettingsLayout && (
            <IconButton onClick={handleBack} sx={{ mr: -1 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box sx={{ position: 'relative' }}>
            <Avatar
              sx={{
                width: 88,
                height: 88,
                fontSize: 36,
                fontWeight: 700,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              {initials}
            </Avatar>
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'divider',
                width: 32,
                height: 32,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <CameraAltOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              {form.first_name} {form.last_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {form.email}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              {form.role && (
                <Chip
                  label={form.role}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                />
              )}
              {form.status && (
                <Chip
                  label={form.status}
                  size="small"
                  color={form.status === 'Active' ? 'success' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Personal Information Card */}
      <Paper sx={cardSx}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5 }}>
          Personal Information
        </Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="First Name"
              name="first_name"
              value={form.first_name}
              onChange={handleFormChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last Name"
              name="last_name"
              value={form.last_name}
              onChange={handleFormChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email Address"
              name="email"
              value={form.email}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiInputBase-input': { color: 'text.secondary' },
              }}
              helperText="Contact your admin to change your email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone Number"
              name="phone"
              value={form.phone}
              onChange={handleFormChange}
              fullWidth
              size="small"
              placeholder="(555) 000-0000"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Display Name"
              name="profile_name"
              value={form.profile_name}
              onChange={handleFormChange}
              fullWidth
              size="small"
              helperText="How your name appears to others"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Timezone"
              name="timezone"
              value={form.timezone}
              onChange={handleFormChange}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Company & Role Card */}
      <Paper sx={cardSx}>
        <Box display="flex" alignItems="center" gap={1} mb={2.5}>
          <BusinessOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle1" fontWeight={700}>
            Company & Role
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company"
              value={userProfile.company_name || ''}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiInputBase-input': { color: 'text.secondary' },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Role"
              value={form.role}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiInputBase-input': { color: 'text.secondary' },
              }}
              helperText="Managed by your firm administrator"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Save Button (non-settings layout) */}
      {!useSettingsLayout && (
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ fontWeight: 600, boxShadow: 'none', px: 4 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}

      {/* Change Password Card */}
      <Divider sx={{ my: 1 }} />
      <Paper sx={cardSx}>
        <Box display="flex" alignItems="center" gap={1} mb={2.5}>
          <LockOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle1" fontWeight={700}>
            Change Password
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Update your password to keep your account secure.
        </Typography>
        {passwordError && (
          <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>
        )}
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField
              label="Current Password"
              name="current_password"
              type="password"
              value={passwordForm.current_password}
              onChange={handlePasswordChange}
              fullWidth
              size="small"
              autoComplete="current-password"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="New Password"
              name="new_password"
              type="password"
              value={passwordForm.new_password}
              onChange={handlePasswordChange}
              fullWidth
              size="small"
              autoComplete="new-password"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Confirm New Password"
              name="confirm_password"
              type="password"
              value={passwordForm.confirm_password}
              onChange={handlePasswordChange}
              fullWidth
              size="small"
              autoComplete="new-password"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              onClick={handlePasswordSave}
              disabled={!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
              sx={{ fontWeight: 600 }}
            >
              Update Password
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );

  return useSettingsLayout ? (
    <>
      <SettingsPageLayout
        title="My Profile"
        description="Your name, contact info, and preferences."
        noCard
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
      >
        {profileContent}
      </SettingsPageLayout>
      <Snackbar
        open={saveToast}
        autoHideDuration={4000}
        onClose={() => setSaveToast(false)}
        message="Profile saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  ) : (
    profileContent
  );
};

export default Profile;
